import React, { useEffect, useState } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Building2, Smartphone, Copy, Check, Upload, FileText, X, AlertCircle } from 'lucide-react';
import { useToast } from './Toast';
import { ApiService } from '../utils/api';
import { formatCurrency } from '../utils/currency';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
const MAX_SIZE = 10 * 1024 * 1024;

export const availablePaymentMethods = (paymentSettings) => {
  const methods = [];
  if (paymentSettings?.upi) methods.push('upi');
  if (paymentSettings?.bank?.accountNo) methods.push('bank');
  return methods;
};

const CopyRow = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const { showSuccess } = useToast();
  if (!value) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (_) {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showSuccess(`${label} copied`);
  };

  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-white border border-gray-200 px-3 py-2">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
        <p className="font-mono text-sm text-gray-900 break-all">{value}</p>
      </div>
      <button type="button" onClick={copy} className="shrink-0 p-1.5 rounded hover:bg-gray-100" aria-label={`Copy ${label}`}>
        {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
      </button>
    </div>
  );
};

// Offline payment collected inside the registration form; reports { paymentMethod, transactionId, documentId }.
const InlinePaymentSection = ({ amount, paymentSettings, eventId, onChange }) => {
  const methods = availablePaymentMethods(paymentSettings);
  const [paymentMethod, setPaymentMethod] = useState(methods.length === 1 ? methods[0] : '');
  const [transactionId, setTransactionId] = useState('');
  const [remark, setRemark] = useState('');
  const [proof, setProof] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const { showError } = useToast();

  useEffect(() => {
    onChange({
      paymentMethod,
      transactionId: transactionId.trim(),
      remark: remark.trim(),
      documentId: proof?.documentId || null,
    });
  }, [paymentMethod, transactionId, remark, proof, onChange]);

  useEffect(() => () => {
    if (proof?.preview) URL.revokeObjectURL(proof.preview);
  }, [proof]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      showError('Upload an image (JPG, PNG, GIF, WebP) or a PDF.');
      return;
    }
    if (file.size > MAX_SIZE) {
      showError('File must be under 10 MB.');
      return;
    }
    setIsUploading(true);
    try {
      const result = await ApiService.uploadFile(file, eventId, 'PAYMENT_PROOF');
      const documentId = result?.documentId || result?.externalId;
      if (!documentId) throw new Error('No documentId returned from upload API');
      setProof({
        documentId,
        name: file.name,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      });
    } catch (err) {
      console.error('Payment screenshot upload error:', err);
      showError('Failed to upload the screenshot. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (methods.length === 0) {
    return (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 flex items-start gap-2 text-sm text-yellow-800">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <p>
          Payment details are not configured for this event yet. You can still register — the organiser will share
          payment details separately.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4 sm:p-5 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-800">Payment</h3>
        <span className="text-sm font-bold text-gray-900">{formatCurrency(amount, 'INR')}</span>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="payment-method">Payment method *</Label>
        <select
          id="payment-method"
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          className="w-full h-10 rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Select how you will pay</option>
          {methods.includes('upi') && <option value="upi">UPI (Google Pay, PhonePe, Paytm…)</option>}
          {methods.includes('bank') && <option value="bank">Bank transfer (NEFT / IMPS / RTGS)</option>}
        </select>
      </div>

      {paymentMethod === 'upi' && (
        <div className="space-y-3">
          <p className="flex items-start gap-2 text-sm text-gray-700">
            <Smartphone className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
            <span>
              Open any UPI app and pay <strong>{formatCurrency(amount, 'INR')}</strong> to the UPI ID below
              {paymentSettings.qrCodeImage ? ' or scan the QR code' : ''}, then add the transaction ID and screenshot.
            </span>
          </p>
          <CopyRow label="UPI ID" value={paymentSettings.upi} />
          {paymentSettings.qrCodeImage && (
            <div className="flex justify-center rounded-lg bg-white border border-gray-200 p-3">
              <img
                src={paymentSettings.qrCodeImage}
                alt="UPI QR code"
                className="w-56 h-56 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
        </div>
      )}

      {paymentMethod === 'bank' && (
        <div className="space-y-3">
          <p className="flex items-start gap-2 text-sm text-gray-700">
            <Building2 className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
            <span>
              Transfer <strong>{formatCurrency(amount, 'INR')}</strong> to this account from your banking app, then add
              the transaction ID and screenshot.
            </span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <CopyRow label="Account holder" value={paymentSettings.bank.accountHolderName} />
            <CopyRow label="Account number" value={paymentSettings.bank.accountNo} />
            <CopyRow label="IFSC" value={paymentSettings.bank.ifsc} />
            <CopyRow label="Bank" value={paymentSettings.bank.name} />
          </div>
        </div>
      )}

      {paymentMethod && (
        <>
          {paymentSettings.instructions && (
            <p className="text-xs text-gray-600 whitespace-pre-line">{paymentSettings.instructions}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="transaction-id">Transaction ID / UTR *</Label>
              <Input
                id="transaction-id"
                placeholder="e.g. 412356789012"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                className="bg-white"
              />
            </div>

            <div className="space-y-1.5 md:order-last md:col-span-2">
              <Label htmlFor="payment-remark">Remark (optional)</Label>
              <Input
                id="payment-remark"
                placeholder="e.g. Paid from father's account / paid for 2 people"
                value={remark}
                maxLength={200}
                onChange={(e) => setRemark(e.target.value)}
                className="bg-white"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="payment-proof-upload">Payment screenshot *</Label>
              {proof ? (
                <div className="flex h-10 items-center justify-between gap-3 rounded-md border border-green-300 bg-green-50 px-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {proof.preview ? (
                      <img src={proof.preview} alt="" className="w-7 h-7 rounded object-cover" />
                    ) : (
                      <FileText className="w-6 h-6 text-gray-400" />
                    )}
                    <span className="text-sm truncate">{proof.name}</span>
                  </div>
                  <button type="button" onClick={() => setProof(null)} className="p-1 rounded hover:bg-green-100" aria-label="Remove screenshot">
                    <X className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="payment-proof-upload"
                  className="flex h-10 items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 bg-white text-sm text-gray-600 cursor-pointer hover:border-blue-400"
                >
                  <Upload className="w-4 h-4" />
                  {isUploading ? 'Uploading…' : 'Upload screenshot (image or PDF)'}
                </label>
              )}
              <input
                id="payment-proof-upload"
                type="file"
                accept="image/*,.pdf"
                onChange={handleFile}
                disabled={isUploading}
                className="hidden"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InlinePaymentSection;
